import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { saveAuthReturnTo, useAuth } from '@repo/common';
import { logoutUser } from '../../api/user/user.api';
import { getJoinInfo, getMyCareerReports, submitJoin } from '../../api/twoDays/join.api';
import TdButton from '../../components/TwoDays/Button';
import { Modal, ModalButton, Select } from '../../components/TwoDays/Modal';
import { JOB_LABEL, cn, initials } from '../../components/TwoDays/model/derive';
import { KST, formatDue } from '../../components/TwoDays/model/time';
import {
  CHIP_GROUPS,
  Chip,
  STACK_ITEMS,
  TIME_ZONES,
  formatTaken,
  slotsFrom,
  toKstRanges,
  tzLabel,
} from '../../components/TwoDays/profileFields';
import HoursRanges, { slotsError } from '../../components/TwoDays/HoursRanges';
import useTitleBadge from '../../components/TwoDays/useTitleBadge';
import { SKILLS_SELF_CHECK_URL } from '../../utils/brand';
import { ReactComponent as ChevronIcon } from '@repo/common/assets/icons/16px/icon_16px_arrow-bottom.svg';
import { CONSENT_ITEMS } from './policy/consentForm';
import { NICKNAME_MAX, randomNickname } from '../../components/TwoDays/model/nickname';
import '../../components/TwoDays/motion.css';
import { ReactComponent as CheckIcon } from '@repo/common/assets/icons/16px/icon_16px_check.svg';

/** 2days Pre-join. 진행 상태는 sessionStorage 에 저장 */

/** 단계 수 — 자가진단 결과 연결에 동의하지 않으면 마지막(자가진단) 단계를 건너뛴다 */
const stepsOf = (form) => (form?.consentSelfCheckLink ? 6 : 5);

const REQUIRED = CONSENT_ITEMS.filter((c) => c.required);

const storageKey = (slug) => `2days-join:${slug}`;

const loadSaved = (slug) => {
  try {
    return JSON.parse(sessionStorage.getItem(storageKey(slug)) || 'null');
  } catch (e) {
    return null;
  }
};

/** 폼 초기값. 합류 전이면 신청서 답에서 툴·스택을 나눠 채운다 */
const initialForm = (me) => {
  const joined = !!me.joinedAt;
  const all = me.tools ?? [];
  return {
    name: me.name ?? '',
    // 실명 표시에 동의하지 않으면 보일 닉네임 — 처음엔 색깔 + 동물로 무작위
    nickname: me.nickname || randomNickname(),
    // 동의서 7항목 (약관 v1.3 제7조). 선택 항목은 기본값 꺼짐
    consentTerms: joined,
    consentDataUse: joined,
    consentPeerReview: joined,
    consentOverseasTransfer: joined,
    consentPromoUse: !!me.consentPromoUse,
    consentRealName: me.nameDisplay === 'real',
    consentSelfCheckLink: !!me.consentSelfCheckLink,
    timezone: me.timezone,
    // 작업 시간 구간 여러 개 — 신청서 값에서 채운다
    slots: slotsFrom(me),
    tools: joined ? all : all.filter((t) => !STACK_ITEMS.includes(t)),
    stack: joined ? (me.stack ?? []) : all.filter((t) => STACK_ITEMS.includes(t)),
  };
};

/* ── 작은 조각 ─────────────────────────────────────────── */

export const PrimaryButton = ({ disabled, full, children, ...props }) => (
  <TdButton
    size='l'
    disabled={disabled}
    {...props}
    className={cn('h-11 px-6 text-[14px]', full && 'h-12 w-full')}
  >
    {children}
  </TdButton>
);

export const GhostButton = ({ children, className, ...props }) => (
  <TdButton
    variant='ghost'
    size='l'
    {...props}
    className={cn(
      'h-11 px-4 text-[14px] text-text-primary hover:bg-green-deep03/[0.06]',
      className,
    )}
  >
    {children}
  </TdButton>
);

const Tag = ({ children }) => (
  <span className='inline-flex h-[22px] shrink-0 items-center whitespace-nowrap rounded-md bg-grayscale-gray01 px-2 text-[12px] font-medium text-text-tertiary shadow-[inset_0_0_0_1px_#d5d2cd]'>
    {children}
  </span>
);

/** 고르는 칩 — 툴·스택 (여러 개) */
const Heading = ({ title, body }) => (
  <div className='flex flex-col gap-2'>
    <h1 className='text-[24px] font-medium leading-[130%] tracking-[-0.01em] text-text-primary'>
      {title}
    </h1>
    {body && <p className='text-[15px] leading-[150%] text-text-secondary'>{body}</p>}
  </div>
);

const FieldLabel = ({ children, required, aside }) => (
  <span className='flex items-center justify-between gap-2 text-[13px] font-medium leading-[140%] text-text-primary'>
    <span>
      {children}
      {required && <span className='text-functional-failed03'>*</span>}
    </span>
    {aside && <span className='font-normal text-text-secondary'>{aside}</span>}
  </span>
);

const Prefilled = ({ children }) => (
  <p className='flex items-center gap-2 rounded-lg bg-green-deep03/[0.05] px-3 py-2.5 text-[13px] leading-[140%] text-text-tertiary'>
    <span aria-hidden className='h-1.5 w-1.5 shrink-0 rounded-full bg-green-deep02' />
    {children}
  </p>
);

const inputClass =
  'h-11 w-full rounded-lg border border-green-deep03/20 bg-grayscale-gray00 px-3 text-[14px] text-text-primary outline-none focus:border-green-deep02';

const Avatar = ({ name, photoUrl, size = 'h-16 w-16 text-[20px]' }) =>
  photoUrl ? (
    <img src={photoUrl} alt='' className={cn('shrink-0 rounded-full object-cover', size)} />
  ) : (
    <span
      className={cn(
        'flex shrink-0 items-center justify-center rounded-full bg-surface-green10 font-medium text-green-deep04',
        size,
      )}
    >
      {name ? initials(name) : ''}
    </span>
  );

/** 번호 원(단계 수만큼) + 연결선. 지난 단계는 ✓ */
const StepIndicator = ({ step, total: STEPS }) => (
  <div className='flex flex-col gap-2'>
    <span className='text-[13px] font-medium leading-[140%] text-text-secondary'>
      Step {step} of {STEPS}
    </span>
    <ol className='flex items-center' aria-label={`Step ${step} of ${STEPS}`}>
      {Array.from({ length: STEPS }, (_, i) => i + 1).map((n) => (
        <li key={n} className={cn('flex items-center', n < STEPS && 'flex-1')}>
          <span
            aria-current={n === step ? 'step' : undefined}
            className={cn(
              'flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[12px] font-medium tabular-nums transition-colors',
              n < step && 'bg-surface-green10 text-green-deep04',
              n === step && 'bg-green-deep04 text-grayscale-gray00',
              n > step && 'bg-green-deep03/[0.06] text-text-secondary',
            )}
          >
            {n < step ? <CheckIcon aria-hidden className='h-4 w-4' /> : n}
          </span>
          {n < STEPS && (
            <span
              aria-hidden
              className={cn(
                'mx-1.5 h-0.5 min-w-2 flex-1 rounded-sm',
                n < step ? 'bg-green-deep03/40' : 'bg-green-deep03/[0.12]',
              )}
            />
          )}
        </li>
      ))}
    </ol>
  </div>
);

/** 안내 화면 (링크 오류·종료·다른 계정) */
export const Notice = ({ title, children, actions }) => (
  <div className='flex min-h-screen flex-col items-center justify-center bg-bg-twodays p-6'>
    <div className='flex w-full max-w-[440px] flex-col items-center gap-3 rounded-[20px] bg-grayscale-gray00 px-8 py-10 text-center shadow-3'>
      <span className='flex h-11 w-11 items-center justify-center rounded-xl bg-green-deep02 text-[15px] font-medium text-grayscale-gray00'>
        2d
      </span>
      <h1 className='text-[20px] font-medium leading-[130%] text-text-primary [text-wrap:pretty]'>
        {title}
      </h1>
      <div className='text-[14px] leading-[150%] text-text-secondary [text-wrap:pretty]'>
        {children}
      </div>
      {actions && <div className='mt-2 flex flex-wrap justify-center gap-2'>{actions}</div>}
    </div>
  </div>
);

/* ── 단계별 본문 ──────────────────────────────────────── */

/** P0 — 팀 링크 첫 화면 */
const EntryStep = ({ info }) => (
  <div className='flex flex-col items-center gap-3.5 text-center'>
    <span className='flex h-[72px] w-[72px] items-center justify-center rounded-[22px] bg-green-deep02 text-[24px] font-medium text-grayscale-gray00 shadow-[0_0_0_6px_rgba(29,195,123,0.14)]'>
      {initials(info.teamName)}
    </span>
    <div className='flex flex-col gap-1.5'>
      <span className='text-[14px] leading-[140%] text-text-secondary'>
        {info.me?.joinedAt
          ? 'You’re on'
          : info.me
            ? 'You’ve been selected for'
            : 'Join your team on'}
      </span>
      <h1 className='text-[26px] font-medium leading-[125%] tracking-[-0.01em] text-text-primary'>
        {info.teamName}
      </h1>
      <span className='text-[14px] leading-[140%] text-text-tertiary'>
        {info.projectName} · 2 days project
      </span>
    </div>
    <div className='flex flex-wrap items-center justify-center gap-x-4 gap-y-1.5 text-[13px] leading-[140%] text-text-secondary'>
      {info.me && (
        <span className='flex items-center gap-1.5'>
          <span className='h-2 w-2 rounded-full bg-green-deep02' />
          {JOB_LABEL[info.me.job]}
          {info.me.leader ? ' · Leader' : ''}
        </span>
      )}
      <span className='flex items-center gap-1.5'>
        <span className='h-2 w-2 rounded-full bg-grayscale-gray05' />
        {info.teamSize} people
      </span>
      <span className='flex items-center gap-1.5'>
        <span className='h-2 w-2 rounded-full bg-grayscale-gray05' />
        Starts {formatDue(new Date(info.teamStartAt), KST)} KST
      </span>
    </div>
    <p className='max-w-[360px] text-[14px] leading-[150%] text-text-secondary [text-wrap:pretty]'>
      {info.me?.joinedAt
        ? 'You’ve finished setting up. You can open the workboard or edit your details.'
        : info.me
          ? 'Set up a few things before your team starts. It takes about 3 minutes.'
          : 'Log in or sign up with your Koready account, using the email you applied with. You’ll come back here automatically.'}
    </p>
  </div>
);

const WelcomeStep = ({ info, user, form, onLogout }) => (
  <>
    <Heading title={`Welcome, ${(form.name || 'there').split(' ')[0]}`} />
    <div className='flex flex-wrap items-center gap-3 rounded-xl border border-green-deep03/[0.12] px-4 py-3.5'>
      <Avatar name={form.name} photoUrl={user?.profileUrl} size='h-10 w-10 text-[14px]' />
      <div className='flex min-w-0 flex-1 flex-col gap-0.5'>
        <span className='truncate text-[15px] font-medium leading-[140%] text-text-primary'>
          {user?.nickname || form.name}
        </span>
        <span className='truncate text-[13px] leading-[140%] text-text-secondary'>
          {info.me.email}
        </span>
      </div>
      <GhostButton onClick={onLogout} className='px-3 text-[13px] text-text-secondary'>
        Not you? Log out
      </GhostButton>
    </div>
    <dl className='grid grid-cols-[auto_minmax(0,1fr)] gap-x-8 gap-y-3 rounded-xl bg-green-deep03/[0.04] p-4 text-[14px] leading-[140%]'>
      <dt className='text-text-secondary'>Project</dt>
      <dd className='font-medium text-text-primary'>{info.projectName}</dd>
      <dt className='text-text-secondary'>Team</dt>
      <dd className='font-medium text-text-primary'>{info.teamName}</dd>
      <dt className='text-text-secondary'>Job</dt>
      <dd className='font-medium text-text-primary'>
        {JOB_LABEL[info.me.job]}
        {info.me.leader ? ' · Leader' : ''}
      </dd>
      <dt className='text-text-secondary'>Team starts</dt>
      <dd className='font-medium text-text-primary'>
        {formatDue(new Date(info.teamStartAt), KST)} KST
      </dd>
    </dl>
    <p className='text-[15px] leading-[150%] text-text-primary'>
      Before you join, let’s set up a few things.
    </p>
  </>
);

const Check = ({ on }) =>
  on ? (
    <span className='flex h-5 w-5 shrink-0 items-center justify-center rounded-md bg-green-deep02 text-grayscale-gray00'>
      <CheckIcon aria-hidden className='h-4 w-4' />
    </span>
  ) : (
    <span className='h-5 w-5 shrink-0 rounded-md bg-grayscale-gray00 shadow-[inset_0_0_0_1.5px_#b1ada8]' />
  );

/** 동의 항목 하나 — 체크박스 줄 + 펼치는 자세한 내용(링크 · 표 · 안내) */
const ConsentItem = ({ item, on, onToggle }) => {
  const [open, setOpen] = useState(false);
  const detailId = `consent-${item.key}`;
  return (
    <div className='rounded-[14px] bg-green-deep03/[0.04]'>
      <div className='flex items-start'>
        <button
          type='button'
          role='checkbox'
          aria-checked={on}
          onClick={onToggle}
          className='flex min-h-14 flex-1 items-start gap-3 rounded-[14px] py-3.5 pl-4 pr-2 text-left transition-colors hover:bg-green-deep03/[0.04]'
        >
          <span className='flex shrink-0 pt-px'>
            <Check on={on} />
          </span>
          <span className='flex-1 text-[14px] leading-[145%] text-text-primary'>
            <span className='mr-1 font-medium'>{item.required ? '[Required]' : '[Optional]'}</span>
            {item.title}
          </span>
        </button>
        <button
          type='button'
          aria-expanded={open}
          aria-controls={detailId}
          aria-label={open ? 'Hide details' : 'Show details'}
          onClick={() => setOpen(!open)}
          className='m-2 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-icon-secondary transition-colors hover:bg-green-deep03/[0.08]'
        >
          <ChevronIcon
            className={cn('h-4 w-4 transition-transform duration-200', open && 'rotate-180')}
          />
        </button>
      </div>
      <div
        id={detailId}
        className='td-collapse'
        data-open={open}
        aria-hidden={!open}
        inert={!open || undefined}
      >
        <div>
          <div className='flex flex-col gap-3 px-4 pb-4 pl-12 text-[13px] leading-[155%] text-text-secondary'>
            {item.links && (
              <p className='flex flex-wrap gap-x-1.5'>
                {item.links.map((l, i) => (
                  <React.Fragment key={l.href}>
                    {i > 0 && <span aria-hidden>·</span>}
                    <Link
                      to={l.href}
                      target='_blank'
                      rel='noopener'
                      className='font-medium text-green-deep03 underline underline-offset-2'
                    >
                      {l.label}
                    </Link>
                  </React.Fragment>
                ))}
              </p>
            )}
            {item.facts?.map((group) => (
              <dl
                key={group[0][0]}
                className='flex flex-col rounded-lg bg-grayscale-gray00 px-3 shadow-[inset_0_0_0_1px_rgba(0,158,79,0.12)]'
              >
                {group.map(([label, value]) => (
                  <div
                    key={label}
                    className='grid gap-0.5 border-t border-green-deep03/[0.08] py-2 first:border-t-0 sm:grid-cols-[132px_minmax(0,1fr)] sm:gap-3'
                  >
                    <dt className='font-medium text-text-primary'>{label}</dt>
                    <dd>{value}</dd>
                  </div>
                ))}
              </dl>
            ))}
            <ul className='flex list-disc flex-col gap-1 pl-4'>
              {item.bullets.map((b) => (
                <li key={b}>{b}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

const ConsentStep = ({ form, setForm }) => {
  const all = CONSENT_ITEMS.every((c) => form[c.key]);
  // 모두 동의 — 선택 항목도 함께 체크. 이미 다 켜져 있으면 모두 끈다
  const toggleAll = () => setForm(Object.fromEntries(CONSENT_ITEMS.map((c) => [c.key, !all])));
  return (
    <>
      {/* 신청 정보 수집 동의와는 별개의 참여 동의서 (#29 · 약관 v1.3 제7조) */}
      <Heading
        title='Consent'
        body='Please read the items below before you start the project. You must agree to all required items to take part. If you do not agree to an optional item, you can still take part.'
      />
      <div className='flex flex-col gap-1.5'>
        <button
          type='button'
          role='checkbox'
          aria-checked={all}
          onClick={toggleAll}
          className='flex min-h-14 items-center gap-3 rounded-[14px] px-4 py-3 text-left shadow-[inset_0_0_0_1px_rgba(0,158,79,0.2)] transition-colors hover:bg-green-deep03/[0.04]'
        >
          <Check on={all} />
          <span className='flex-1 text-[15px] font-medium leading-[140%] text-text-primary'>
            Agree to all
          </span>
        </button>
        {CONSENT_ITEMS.map((c) => (
          <ConsentItem
            key={c.key}
            item={c}
            on={!!form[c.key]}
            onToggle={() => setForm({ [c.key]: !form[c.key] })}
          />
        ))}
      </div>
      <p className='text-[13px] leading-[150%] text-text-secondary'>
        To withdraw an optional item, email{' '}
        <a href='mailto:contact@ko-ready.com' className='text-text-primary underline'>
          contact@ko-ready.com
        </a>
        .
      </p>
    </>
  );
};

const ProfileStep = ({ form, setForm, me, photoUrl }) => (
  <>
    <Heading title='Your profile' body='Check how your teammates will see you.' />
    <div className='flex items-center gap-4'>
      <Avatar name={form.consentRealName ? form.name : form.nickname} photoUrl={photoUrl} />
      <span className='text-[13px] leading-[140%] text-text-secondary'>
        Your photo comes from your Koready account.
      </span>
    </div>
    {form.consentRealName ? (
      <label className='flex flex-col gap-1.5'>
        <FieldLabel required>Name</FieldLabel>
        <input
          value={form.name}
          maxLength={100}
          onChange={(e) => setForm({ name: e.target.value })}
          placeholder='Your full name'
          className={inputClass}
        />
      </label>
    ) : (
      // 실명 표시에 동의하지 않았으면 — 이름 대신 팀에게 보일 닉네임 (무작위로 채우고 고칠 수 있다)
      <div className='flex flex-col gap-1.5'>
        <FieldLabel required>Nickname</FieldLabel>
        <div className='flex gap-2'>
          <input
            value={form.nickname}
            maxLength={NICKNAME_MAX}
            onChange={(e) => setForm({ nickname: e.target.value })}
            placeholder='e.g. Teal Lynx'
            aria-label='Nickname'
            className={cn(inputClass, 'min-w-0 flex-1')}
          />
          <button
            type='button'
            onClick={() => setForm({ nickname: randomNickname(form.nickname) })}
            className='h-11 shrink-0 rounded-lg px-3 text-[14px] font-medium text-text-primary shadow-[inset_0_0_0_1px_rgba(0,158,79,0.2)] hover:bg-green-deep03/[0.04]'
          >
            Shuffle
          </button>
        </div>
        <span className='text-[13px] leading-[140%] text-text-secondary'>
          Your teammates see this name instead of your real name. Your certificate still shows
          your real name. You can change the nickname later in Profile.
        </span>
      </div>
    )}
    {/* 직군은 운영진이 지정 (보기 전용) */}
    <div className='flex flex-col gap-1.5'>
      <FieldLabel>Your job</FieldLabel>
      <div className='flex h-11 items-center rounded-lg bg-green-deep03/[0.04] px-3 text-[14px] text-text-primary'>
        {JOB_LABEL[me.job]}
        {me.leader ? ' · Leader' : ''}
      </div>
      <span className='text-[13px] leading-[140%] text-text-secondary'>
        Set by the organizers when they picked your team.
      </span>
    </div>
  </>
);

const HoursStep = ({ form, setForm, info, joined }) => {
  const zones = TIME_ZONES.includes(form.timezone) ? TIME_ZONES : [form.timezone, ...TIME_ZONES];
  const kst = toKstRanges(form.timezone, form.slots, new Date(info.teamStartAt));
  return (
    <>
      <Heading
        title='Working hours'
        body='When can you work during the project? Your team sees these hours in Korea time.'
      />
      {!joined && <Prefilled>Filled in from your application. You can change it.</Prefilled>}
      <label className='flex flex-col gap-1.5'>
        <FieldLabel>Time zone</FieldLabel>
        <Select
          value={form.timezone}
          onChange={(e) => setForm({ timezone: e.target.value })}
          className='h-11'
        >
          {zones.map((tz) => (
            <option key={tz} value={tz}>
              {tzLabel(tz)}
            </option>
          ))}
        </Select>
      </label>
      <div className='flex flex-col gap-1.5'>
        <FieldLabel>Hours</FieldLabel>
        {/* 여러 구간 (예: 10:00–12:00, 15:00–20:00) · 최대 5개 */}
        <HoursRanges
          slots={form.slots}
          onChange={(slots) => setForm({ slots })}
          fieldClassName='h-11 w-[124px]'
        />
      </div>
      <div className='flex flex-col gap-1.5 rounded-xl bg-green-deep03/[0.06] p-4'>
        <span className='text-[13px] leading-[140%] text-text-secondary'>In Korea time (KST)</span>
        <div className='flex flex-wrap items-center gap-x-3 gap-y-2'>
          <span className='text-[22px] font-medium leading-[130%] text-text-primary tabular-nums'>
            {kst.text}
          </span>
          {kst.crossesDay && <Tag>Ends next day</Tag>}
        </div>
      </div>
      <div className='flex flex-col gap-1'>
        {/* 권장 시간은 아직 미정 — 기획 확정 후 숫자를 채운다 */}
        <p className='text-[14px] font-medium leading-[150%] text-text-primary'>
          We recommend setting at least [TBD] hours of work time.
        </p>
        <p className='text-[14px] leading-[150%] text-text-secondary [text-wrap:pretty]'>
          These hours decide when you show as offline, how long task requests wait for you, and when
          your 3-hour peer review window runs.
        </p>
      </div>
    </>
  );
};

const ToolsStep = ({ form, setForm, joined }) => (
  <>
    <Heading title='Tools and stack' body='Pick what you use. This is self-reported.' />
    {!joined && <Prefilled>Filled in from your application. You can change it.</Prefilled>}
    {CHIP_GROUPS.map((g) => {
      const items = [...g.items, ...form[g.key].filter((x) => !g.items.includes(x))];
      return (
        <div key={g.key} className='flex flex-col gap-2.5'>
          <span className='text-[13px] font-medium leading-[140%] text-text-primary'>
            {g.title}
          </span>
          <div className='flex flex-wrap gap-2'>
            {items.map((item) => {
              const on = form[g.key].includes(item);
              return (
                <Chip
                  key={item}
                  on={on}
                  onClick={() =>
                    setForm({
                      [g.key]: on ? form[g.key].filter((x) => x !== item) : [...form[g.key], item],
                    })
                  }
                >
                  {item}
                </Chip>
              );
            })}
          </div>
        </div>
      );
    })}
    <p className='text-[14px] leading-[150%] text-text-secondary'>
      You can edit this later in Profile.
    </p>
  </>
);

const SelfCheckStep = ({ report, state, opened, onRefresh }) => (
  <>
    <Heading
      title='See yourself the way your team does'
      body='After 2 days, we put your self-check next to your teammates’ reviews. Your growth report shows where you’re spot on and where you’re underrating yourself.'
    />
    {state === 'loading' && (
      <div
        className='h-[88px] animate-pulse rounded-xl bg-green-deep03/[0.04]'
        aria-label='Loading'
      />
    )}
    {state !== 'loading' && report && (
      <div className='flex flex-col gap-2 rounded-xl border border-green-deep03/[0.12] p-4'>
        <div className='flex flex-wrap justify-between gap-x-3 gap-y-1'>
          <span className='text-[15px] font-medium leading-[140%] text-text-primary'>
            Career self-check
          </span>
          <span className='text-[13px] leading-[140%] text-text-secondary'>
            Taken {formatTaken(report.createdAt)}
          </span>
        </div>
        <span className='text-[14px] leading-[150%] text-text-tertiary'>
          {[report.careerType, report.matchPct ? `${report.matchPct}% match` : null]
            .filter(Boolean)
            .join(' · ')}
        </span>
      </div>
    )}
    {state !== 'loading' && !report && (
      <div className='flex flex-col items-start gap-3 rounded-xl bg-green-deep03/[0.04] p-4 text-[14px] leading-[150%] text-text-secondary'>
        <span>
          {state === 'error'
            ? 'We couldn’t load your self-check results.'
            : 'No self-check result on your Koready account yet.'}
        </span>
        {(opened || state === 'error') && (
          <TdButton variant='soft' onClick={onRefresh} className='px-3.5 text-[13px]'>
            {state === 'error' ? 'Try again' : 'I’ve finished it — check again'}
          </TdButton>
        )}
      </div>
    )}
  </>
);

/* ── 페이지 ──────────────────────────────────────────── */

const TwoDaysJoin = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user, isLoggedIn, loading: authLoading } = useAuth() ?? {};
  useTitleBadge(0);

  const [info, setInfo] = useState(null);
  const [loadState, setLoadState] = useState('loading'); // loading · ready · notFound · error
  const [saved] = useState(() => loadSaved(slug));
  const [step, setStep] = useState(saved?.step ?? 0);
  // 예전에 저장된 진행 중 폼(구간 하나: from · to)도 구간 목록으로 읽는다
  const [form, setFormState] = useState(() => {
    if (!saved?.form) return null;
    const f = saved.form.slots
      ? saved.form
      : { ...saved.form, slots: [{ from: saved.form.from, to: saved.form.to }] };
    // 닉네임이 생기기 전에 저장해 둔 폼 — 무작위로 채운다
    return f.nickname ? f : { ...f, nickname: randomNickname() };
  },
  );
  const [skipDialog, setSkipDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [report, setReport] = useState(null);
  const [reportState, setReportState] = useState('idle'); // idle · loading · ready · error
  const [selfCheckOpened, setSelfCheckOpened] = useState(false);

  useEffect(() => {
    if (authLoading) return undefined;
    let alive = true;
    setLoadState('loading');
    getJoinInfo(slug)
      .then((d) => {
        if (!alive) return;
        setInfo(d);
        setLoadState('ready');
      })
      .catch((e) => alive && setLoadState(e.response?.status === 404 ? 'notFound' : 'error'));
    return () => {
      alive = false;
    };
  }, [slug, authLoading, isLoggedIn]);

  const me = info?.me ?? null;

  // 이미 합류했으면 Pre-join 을 다시 보여주지 않고 바로 보드로 (내 정보 수정은 보드의 Profile 에서)
  useEffect(() => {
    if (!me?.joinedAt) return;
    sessionStorage.removeItem(storageKey(slug));
    navigate(`/2days/board/${slug}`, { replace: true });
  }, [me?.joinedAt, slug, navigate]);

  useEffect(() => {
    if (me && !form) setFormState(initialForm(me));
  }, [me, form]);

  // 로그인하고 돌아왔으면 P0 을 건너뛴다
  useEffect(() => {
    if (me && saved?.pendingLogin) setStep((s) => (s === 0 ? 1 : s));
  }, [me, saved]);

  useEffect(() => {
    if (form) sessionStorage.setItem(storageKey(slug), JSON.stringify({ step, form }));
  }, [slug, step, form]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [step]);

  const loadReports = useCallback(() => {
    setReportState('loading');
    getMyCareerReports()
      .then((list) => {
        const all = Array.isArray(list) ? list : [];
        setReport(all.find((r) => String(r.id) === String(me?.careerReportId)) ?? all[0] ?? null);
        setReportState('ready');
      })
      .catch(() => setReportState('error'));
  }, [me?.careerReportId]);

  useEffect(() => {
    if (step === 6 && reportState === 'idle') loadReports();
  }, [step, reportState, loadReports]);

  const setForm = (patch) => setFormState((f) => ({ ...f, ...patch }));

  const logout = async () => {
    sessionStorage.removeItem(storageKey(slug));
    try {
      await logoutUser();
    } finally {
      window.location.assign(`/2days/join/${slug}`);
    }
  };

  const startLogin = () => {
    const returnTo = `/2days/join/${slug}`;
    sessionStorage.setItem(
      storageKey(slug),
      JSON.stringify({ step: 0, form: null, pendingLogin: true }),
    );
    saveAuthReturnTo(returnTo);
    navigate('/login', { state: { returnTo } });
  };

  const openBoard = (tour) =>
    navigate(`/2days/board/${slug}${tour ? '?tour=1' : ''}`, { replace: true });

  const submit = async (careerReportId) => {
    setSubmitting(true);
    setSubmitError('');
    try {
      await submitJoin(slug, {
        // 실명은 수료증에 쓰니 닉네임을 골라도 그대로 보낸다 (비어 있으면 닉네임)
        name: form.name.trim() || form.nickname.trim(),
        consentTerms: !!form.consentTerms,
        consentDataUse: !!form.consentDataUse,
        consentPeerReview: !!form.consentPeerReview,
        consentOverseasTransfer: !!form.consentOverseasTransfer,
        consentPromoUse: !!form.consentPromoUse,
        consentSelfCheckLink: !!form.consentSelfCheckLink,
        // 실명 표시 동의 → real, 아니면 닉네임
        nameDisplay: form.consentRealName ? 'real' : 'anon',
        nickname: form.consentRealName ? null : form.nickname.trim(),
        timezone: form.timezone,
        // 첫 구간은 예전 필드로도 보낸다
        availableFrom: form.slots[0].from,
        availableTo: form.slots[0].to,
        availableSlots: form.slots,
        tools: form.tools,
        stack: form.stack,
        // 자가진단 결과는 연결에 동의했을 때만 보낸다
        careerReportId: form.consentSelfCheckLink ? (careerReportId ?? null) : null,
      });
      sessionStorage.removeItem(storageKey(slug));
      // 첫 합류면 코치마크 표시
      openBoard(!me.joinedAt);
    } catch (e) {
      setSkipDialog(false);
      const status = e.response?.status;
      setSubmitError(
        status === 403
          ? 'This account isn’t on this team. Log in with the email you applied with.'
          : status === 409
            ? 'This project has ended.'
            : 'We couldn’t save your details. Please try again.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  /* ── 진입 불가 ── */

  if (authLoading || loadState === 'loading' || (me && !form) || me?.joinedAt) {
    return <div className='min-h-screen bg-bg-twodays' />;
  }
  if (loadState === 'notFound') {
    return (
      <Notice title='This link doesn’t work'>
        Open the team link from your selection email, or contact the organizers.
      </Notice>
    );
  }
  if (loadState === 'error') {
    return (
      <Notice
        title='Something went wrong'
        actions={<PrimaryButton onClick={() => window.location.reload()}>Try again</PrimaryButton>}
      >
        We couldn’t load this page.
      </Notice>
    );
  }
  if (info.status === 'ended') {
    return (
      <Notice title={`${info.projectName} has ended`}>
        This project is over, so you can’t join anymore.
      </Notice>
    );
  }
  if (isLoggedIn && !me) {
    return (
      <Notice
        title={
          info.otherTeamName
            ? `You’re on ${info.otherTeamName}`
            : 'This link is for selected members'
        }
        actions={
          <GhostButton onClick={logout} className='bg-green-deep03/[0.08]'>
            Log in with another account
          </GhostButton>
        }
      >
        {info.otherTeamName
          ? `This link is for ${info.teamName}. Open the link for ${info.otherTeamName} from your selection email.`
          : `You’re logged in as ${user?.email || user?.socialEmail || 'another account'}, which isn’t on ${info.teamName}. Log in with the email you applied with.`}
      </Notice>
    );
  }

  /* ── 다음 버튼 ── */

  let primary = {
    label: 'Continue',
    onClick: () => setStep(step + 1),
    disabled: false,
    reason: '',
  };
  if (step === 0) {
    primary = me?.joinedAt
      ? { ...primary, label: 'Open workboard', onClick: () => openBoard(false) }
      : {
          ...primary,
          label: me ? 'Continue' : 'Continue with Koready',
          onClick: me ? () => setStep(1) : startLogin,
        };
  } else if (step === 1) {
    primary.label = 'Get started';
  } else if (step === 2) {
    primary = {
      ...primary,
      disabled: REQUIRED.some((c) => !form[c.key]),
      reason: 'Check all required items to continue.',
    };
  } else if (step === 3) {
    primary = form.consentRealName
      ? { ...primary, disabled: !form.name.trim(), reason: 'Enter your name to continue.' }
      : { ...primary, disabled: !form.nickname.trim(), reason: 'Enter a nickname to continue.' };
  } else if (step === 4) {
    primary = {
      ...primary,
      disabled: !!slotsError(form.slots),
      reason: slotsError(form.slots) ?? '',
    };
  } else if (step === 5 && !form.consentSelfCheckLink) {
    // 자가진단 연결에 동의하지 않았으면 여기서 합류
    primary = {
      ...primary,
      label: 'Join project',
      disabled: submitting,
      onClick: () => submit(null),
    };
  } else if (step === 6) {
    primary = report
      ? {
          ...primary,
          label: me.joinedAt ? 'Save' : 'Use this result',
          onClick: () => submit(report.id),
        }
      : {
          ...primary,
          label: 'Take self-check',
          disabled: reportState === 'loading',
          onClick: () => {
            window.open(SKILLS_SELF_CHECK_URL, '_blank', 'noopener');
            setSelfCheckOpened(true);
          },
        };
    if (submitting) primary.disabled = true;
  }

  const isEntry = step === 0;

  return (
    <div
      className={cn(
        'flex min-h-screen flex-col items-center bg-bg-twodays px-3 pt-5 sm:px-6 sm:pb-[72px] sm:pt-14',
        isEntry && 'sm:justify-center',
      )}
    >
      <div
        className={cn('flex w-full flex-col gap-4', isEntry ? 'max-w-[480px]' : 'max-w-[560px]')}
      >
        <div className='flex items-center gap-2.5'>
          <span className='flex h-8 w-8 shrink-0 items-center justify-center rounded-[9px] bg-green-deep02 text-[12px] font-medium text-grayscale-gray00'>
            2d
          </span>
          <span className='text-[15px] font-medium leading-[140%] text-text-primary'>
            2 days project
          </span>
        </div>

        {!isEntry && <StepIndicator step={step} total={stepsOf(form)} />}

        <div className='flex flex-col gap-7 rounded-t-[20px] bg-grayscale-gray00 px-5 pt-6 shadow-3 sm:rounded-[20px] sm:p-10'>
          {step === 0 && <EntryStep info={info} />}
          {step === 1 && <WelcomeStep info={info} user={user} form={form} onLogout={logout} />}
          {step === 2 && <ConsentStep form={form} setForm={setForm} />}
          {step === 3 && (
            <ProfileStep form={form} setForm={setForm} me={me} photoUrl={user?.profileUrl} />
          )}
          {step === 4 && (
            <HoursStep form={form} setForm={setForm} info={info} joined={!!me.joinedAt} />
          )}
          {step === 5 && <ToolsStep form={form} setForm={setForm} joined={!!me.joinedAt} />}
          {step === 6 && (
            <SelfCheckStep
              report={report}
              state={reportState}
              opened={selfCheckOpened}
              onRefresh={loadReports}
            />
          )}

          {/* 모바일은 하단 버튼 바를 고정한다 */}
          <div className='sticky bottom-0 z-[2] -mx-5 flex flex-col gap-2.5 border-t border-green-deep03/[0.08] bg-grayscale-gray00 px-5 pb-5 pt-3 sm:static sm:mx-0 sm:px-0 sm:pb-0 sm:pt-5'>
            {submitError && (
              <span
                role='alert'
                className='text-right text-[13px] leading-[140%] text-functional-failed04'
              >
                {submitError}
              </span>
            )}
            {!submitError && primary.disabled && primary.reason && (
              <span className='text-right text-[13px] leading-[140%] text-text-secondary'>
                {primary.reason}
              </span>
            )}
            {isEntry ? (
              <div className='flex flex-col gap-2'>
                <PrimaryButton full onClick={primary.onClick}>
                  {primary.label}
                </PrimaryButton>
                {me?.joinedAt && (
                  <GhostButton onClick={() => setStep(1)} className='w-full'>
                    Edit my details
                  </GhostButton>
                )}
              </div>
            ) : (
              <div className='flex items-center gap-2'>
                {step >= 2 && <GhostButton onClick={() => setStep(step - 1)}>Back</GhostButton>}
                <span className='flex-1' />
                {step === 6 && !report && reportState !== 'loading' && (
                  <GhostButton
                    onClick={() => setSkipDialog(true)}
                    className='px-3 text-text-secondary'
                  >
                    Skip for now
                  </GhostButton>
                )}
                <PrimaryButton onClick={primary.onClick} disabled={primary.disabled}>
                  {submitting ? 'Saving…' : primary.label}
                </PrimaryButton>
              </div>
            )}
          </div>
        </div>
      </div>

      {skipDialog && (
        <Modal
          title='Skip self-check?'
          subtitle='You can link it later, but your report will miss this comparison.'
          onClose={() => setSkipDialog(false)}
          footer={
            <>
              <ModalButton onClick={() => submit(null)} disabled={submitting}>
                Skip
              </ModalButton>
              <ModalButton
                primary
                onClick={() => {
                  setSkipDialog(false);
                  window.open(SKILLS_SELF_CHECK_URL, '_blank', 'noopener');
                  setSelfCheckOpened(true);
                }}
              >
                Take self-check
              </ModalButton>
            </>
          }
        />
      )}
    </div>
  );
};

export default TwoDaysJoin;
